import React from "react";
import { Popup, Table } from "semantic-ui-react";
import styled from "styled-components";
import { Condition } from "../../../../util";
import { StyledHeader } from "../ActionsV3/SelectableItem";
import {
  OptionType,
  convertDecimalToRoman,
  getSelectedPhaseIndex,
} from "../util";
import {
  NewActionWrapper,
  PhaseData,
  StyledNonBoldHeader,
  SuccessRateTriggerType,
  TimestampTriggerType,
} from "./NewAction";

const SummaryGridItem = styled.div`
  display: flex;
  width: "100%";
  align-items: center;
  height: "100%";
  padding: 10px 0;
`;

interface SummaryComponentProps {
  phasesData: PhaseData[];
  isPhasedRollout: boolean;
  action: string;
  optionType: OptionType;
  selectedVersions: {
    id: number;
    value: string;
    device_component?: string;
    bundle_version?: string;
  }[];
}

const SummaryComponent: React.FC<SummaryComponentProps> = ({
  phasesData,
  isPhasedRollout,
  action,
  optionType,
  selectedVersions,
}) => {
  const getFilterInfo = (
    phase: PhaseData
  ): { key: string; value: string }[] => {
    const filterInfo: { key: string; value: string }[] = [];

    if (phase.info.filter) {
      for (const [key, value] of Object.entries(phase.info.filter)) {
        const formattedValue = Array.isArray(value)
          ? value.join(", ")
          : String(value);
        filterInfo.push({ key, value: formattedValue });
      }
    }

    return filterInfo;
  };

  const formatStateFilterCondition = (condition: Condition): string => {
    if (typeof condition !== "object" || condition === null) {
      return "";
    }

    if ("operator" in condition) {
      if (condition.operator === "no_data") {
        const threshold = (condition as any).threshold_seconds;
        return threshold ? `no_data (threshold: ${threshold}s)` : "no_data";
      } else if (condition.operator === "and" || condition.operator === "or") {
        const formattedConditions = condition.conditions
          .map(formatStateFilterCondition)
          .filter((c) => c !== "")
          .join(` ${condition.operator.toUpperCase()} `);
        return formattedConditions ? `(${formattedConditions})` : "";
      }
    }
    // SimpleCondition
    if (
      "field" in condition &&
      "operator" in condition &&
      "value" in condition
    ) {
      const valueStr =
        condition.value === null
          ? "null"
          : Array.isArray(condition.value)
            ? condition.value.join(", ")
            : String(condition.value);
      return `${condition.field} ${condition.operator} ${valueStr}`;
    }
    return "";
  };

  const formatQueryTypeStateFilter = (queryType: any): string => {
    // Check if it's a QueryType format: ["and", ["=", {column: "x"}, {value: "y"}], ...]
    if (
      Array.isArray(queryType) &&
      queryType.length > 0 &&
      (queryType[0] === "and" || queryType[0] === "or")
    ) {
      const operator = queryType[0].toUpperCase();
      const conditions: string[] = [];

      for (let i = 1; i < queryType.length; i++) {
        const condition = queryType[i];
        if (
          Array.isArray(condition) &&
          condition.length === 3 &&
          typeof condition[1] === "object" &&
          condition[1].column &&
          typeof condition[2] === "object" &&
          "value" in condition[2]
        ) {
          const op = condition[0];
          const column = condition[1].column;
          const value =
            condition[2].value === null ? "null" : String(condition[2].value);
          conditions.push(`${column} ${op} ${value}`);
        }
      }

      return conditions.length > 0 ? conditions.join(` ${operator} `) : "";
    }
    return "";
  };

  const getStateFilterInfo = (phase: PhaseData): string[] => {
    const stateFilter = phase.info.state_filter;
    if (!stateFilter || !Array.isArray(stateFilter)) {
      return [];
    }

    if (stateFilter.length === 0) {
      return [];
    }

    // Check if it's QueryType format (array starting with "and" or "or")
    // TypeScript typing might say Condition[], but runtime could be QueryType
    const firstItem = stateFilter[0] as any;
    if (
      typeof firstItem === "string" &&
      (firstItem === "and" || firstItem === "or")
    ) {
      // It's a single QueryType array
      const formatted = formatQueryTypeStateFilter(stateFilter);
      return formatted ? [formatted] : [];
    }

    // It's an array of Condition objects or an array of QueryType arrays
    const results: string[] = [];
    for (const item of stateFilter) {
      if (
        Array.isArray(item) &&
        typeof item[0] === "string" &&
        (item[0] === "and" || item[0] === "or")
      ) {
        // It's a QueryType array
        const formatted = formatQueryTypeStateFilter(item);
        if (formatted) {
          results.push(formatted);
        }
      } else if (typeof item === "object" && item !== null) {
        // Try Condition format
        const formatted = formatStateFilterCondition(item as Condition);
        if (formatted) {
          results.push(formatted);
        }
      }
    }
    return results;
  };

  const getPhasesDeviceCount = (phase: PhaseData) => {
    const { type, filter, device_ids, fraction } = phase.info;
    const metadataFilters = getFilterInfo(phase);
    const stateFilters = getStateFilterInfo(phase);
    const hasMetadataFilters = metadataFilters.length > 0;
    const hasStateFilters = stateFilters.length > 0;

    if (type === "fixed-list") {
      if (filter && Object.keys(filter)?.length > 0) {
        return (
          <Popup
            inverted
            content={<p>Devices: {device_ids?.join(", ")}</p>}
            trigger={
              <div>
                {hasMetadataFilters &&
                  metadataFilters.map((filterItem) => (
                    <div key={filterItem.key}>
                      <strong>{filterItem.key}:</strong> {filterItem.value}
                    </div>
                  ))}
                {hasStateFilters && (
                  <div
                    style={{ marginTop: hasMetadataFilters ? "8px" : "0px" }}
                  >
                    <strong>State Filters:</strong>
                    {stateFilters.map((stateFilter, index) => (
                      <div key={index} style={{ marginLeft: "12px" }}>
                        {stateFilter}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            }
          />
        );
      } else if (device_ids && device_ids?.length > 0) {
        return (
          <div>
            <strong>Device-Id:</strong> {`${device_ids.join(", ")}`}
            {hasStateFilters && (
              <div style={{ marginTop: "8px" }}>
                <strong>State Filters:</strong>
                {stateFilters.map((stateFilter, index) => (
                  <div key={index} style={{ marginLeft: "12px" }}>
                    {stateFilter}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      }
    } else if (type === "filter-fraction" || type === "filter-fraction-lazy") {
      if (
        filter &&
        Object.keys(filter)?.length === 0 &&
        !device_ids &&
        !hasStateFilters
      ) {
        return "All devices are selected";
      } else {
        return (
          <div>
            <h4
              style={{ margin: "0px" }}
            >{`${fraction}% devices are selected from filter: `}</h4>
            {hasMetadataFilters &&
              metadataFilters.map((filterItem, index) => (
                <div key={filterItem.key}>
                  <strong>{filterItem.key}:</strong> {filterItem.value}
                </div>
              ))}
            {hasStateFilters && (
              <div style={{ marginTop: hasMetadataFilters ? "8px" : "0px" }}>
                <strong>State Filters:</strong>
                {stateFilters.map((stateFilter, index) => (
                  <div key={index} style={{ marginLeft: "12px" }}>
                    {stateFilter}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      }
    }

    // If only state filters exist without other filters
    if (hasStateFilters && !hasMetadataFilters && !device_ids) {
      return (
        <div>
          <strong>State Filters:</strong>
          {stateFilters.map((stateFilter, index) => (
            <div key={index} style={{ marginLeft: "12px" }}>
              {stateFilter}
            </div>
          ))}
        </div>
      );
    }

    return "No devices selected";
  };

  const getPhasesTriggerInfo = (phase: PhaseData) => {
    let phaseTriggerInfo = "";
    if (
      (phase.trigger_on as TimestampTriggerType).timestamp <= new Date() &&
      phase.name === "Phase I"
    ) {
      phaseTriggerInfo = "Immediately";
    } else if ((phase.trigger_on as TimestampTriggerType).timestamp) {
      phaseTriggerInfo = (
        phase.trigger_on as TimestampTriggerType
      ).timestamp?.toLocaleString("en-GB");
    } else if (
      (phase.trigger_on as SuccessRateTriggerType).success_percentage
    ) {
      phaseTriggerInfo = `When ${
        (phase.trigger_on as SuccessRateTriggerType).success_percentage
      }% success rate in Phase ${convertDecimalToRoman(getSelectedPhaseIndex(phase.name))}`;
    } else {
      phaseTriggerInfo = "Invalid trigger type";
    }
    return phaseTriggerInfo;
  };

  return (
    <NewActionWrapper>
      <StyledHeader as="h2" style={{ marginTop: "0px", marginBottom: "30px" }}>
        Summary
      </StyledHeader>
      <div
        style={{
          paddingLeft: "15px",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateAreas: `"auto auto auto auto"`,
            marginBottom: "30px",
          }}
        >
          <SummaryGridItem>
            <StyledNonBoldHeader
              as="h3"
              style={{
                marginTop: "0px",
                marginBottom: "0px",
                fontSize: "1.1rem",
                whiteSpace: "nowrap",
              }}
            >
              Action Type:
            </StyledNonBoldHeader>
          </SummaryGridItem>
          <SummaryGridItem>
            {action !== "" ? action : "No action selected"}
          </SummaryGridItem>
          {(optionType === OptionType.ChooseFirmware ||
            optionType === OptionType.UploadFirmware ||
            optionType === OptionType.ChooseConfig ||
            optionType === OptionType.UploadConfig ||
            optionType === OptionType.ChooseGeofence ||
            optionType === OptionType.UploadGeofence) && (
            <>
              <SummaryGridItem>
                <StyledNonBoldHeader
                  as="h3"
                  style={{
                    marginTop: "0px",
                    marginBottom: "0px",
                    fontSize: "1.1rem",
                    whiteSpace: "nowrap",
                  }}
                >
                  Version:
                </StyledNonBoldHeader>
              </SummaryGridItem>
              <SummaryGridItem style={{ flexDirection: "column" }}>
                {selectedVersions.map((selectedVersion) => {
                  let version =
                    selectedVersion.value !== ""
                      ? selectedVersion.value
                      : "No version selected";

                  if (
                    selectedVersion.device_component &&
                    selectedVersion.device_component !== "" &&
                    selectedVersion.device_component !==
                      "update_firmware with bundle"
                  ) {
                    version = `${version} (${selectedVersion.device_component})`;
                  }
                  if (selectedVersion.bundle_version) {
                    version = selectedVersion.bundle_version;
                  }
                  return <SummaryGridItem>{version}</SummaryGridItem>;
                })}
              </SummaryGridItem>
            </>
          )}
          <SummaryGridItem>
            <StyledNonBoldHeader
              as="h3"
              style={{
                marginTop: "0px",
                marginBottom: "0px",
                fontSize: "1.1rem",
                whiteSpace: "nowrap",
              }}
            >
              Start Date/Time:
            </StyledNonBoldHeader>
          </SummaryGridItem>
          <SummaryGridItem>
            {(phasesData[0].trigger_on as TimestampTriggerType).timestamp >
              new Date() &&
            (phasesData[0].trigger_on as TimestampTriggerType).timestamp
              ? (
                  phasesData[0].trigger_on as TimestampTriggerType
                ).timestamp?.toLocaleString("en-GB")
              : "Immediately"}
          </SummaryGridItem>
        </div>

        <Table>
          <Table.Header>
            <Table.Row>
              {isPhasedRollout && (
                <Table.Cell
                  style={{
                    borderBottom: "1px solid #3B3F43",
                  }}
                >
                  Phases
                </Table.Cell>
              )}
              <Table.Cell
                style={{
                  borderBottom: "1px solid #3B3F43",
                }}
              >
                Devices/Filters
              </Table.Cell>
              <Table.Cell
                style={{
                  borderBottom: "1px solid #3B3F43",
                }}
              >
                When to Trigger
              </Table.Cell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {phasesData.flatMap((phase) => (
              <Table.Row key={phase.id}>
                {isPhasedRollout && <Table.Cell>{phase.name}</Table.Cell>}
                <Table.Cell>{getPhasesDeviceCount(phase)}</Table.Cell>
                <Table.Cell>{getPhasesTriggerInfo(phase)}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>
    </NewActionWrapper>
  );
};

export default SummaryComponent;
